import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { THEMES } from "@starquest/core";
import styles from "./Landing.module.css";

const CYCLE_IDS = ["explorer", "wizard", "sports"] as const;

const THEME_TAGLINES: Record<string, string> = {
  explorer: "Earthy greens",
  wizard:   "Deep purple",
  sports:   "Bold orange",
};

export default function Landing() {
  const navigate = useNavigate();
  const [themeIdx,  setThemeIdx]  = useState(0);
  const [loginOpen, setLoginOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (!loginOpen || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setDropPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    function onDown(e: MouseEvent) {
      if (!(e.target as Element).closest("[data-login-menu]")) setLoginOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [loginOpen]);

  useEffect(() => {
    const id = setInterval(() => setThemeIdx((i) => (i + 1) % CYCLE_IDS.length), 2500);
    return () => clearInterval(id);
  }, []);

  const active = THEMES[CYCLE_IDS[themeIdx]];

  function scrollToSection(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <div className={styles.page}>

      {/* ① HERO */}
      <section className={styles.hero}>
        <nav className={styles.nav}>
          <span className={styles.logo}>✨ StarQuest</span>
          <div className={styles.navLinks}>
            <button className={styles.navLink} onClick={() => scrollToSection("how-it-works")}>How it works</button>
            <button className={styles.navLink} onClick={() => scrollToSection("themes")}>Themes</button>
            <div className={styles.loginWrap} data-login-menu>
              <button ref={btnRef} className={styles.ghostBtn} onClick={() => setLoginOpen((v) => !v)}>
                Log In {loginOpen ? "▲" : "▼"}
              </button>
            </div>
            {loginOpen && createPortal(
              <div
                data-login-menu
                className={styles.loginDropdown}
                style={{ top: dropPos.top, right: dropPos.right }}
              >
                <button className={styles.loginOption} onClick={() => { setLoginOpen(false); navigate("/login"); }}>
                  <span className={styles.loginOptionIcon}>👨</span>
                  <span>
                    <span className={styles.loginOptionTitle}>Parent</span>
                    <span className={styles.loginOptionSub}>Email + one-time code</span>
                  </span>
                </button>
                <div className={styles.loginDivider} />
                <button className={styles.loginOption} onClick={() => { setLoginOpen(false); navigate("/kid/login"); }}>
                  <span className={styles.loginOptionIcon}>⭐</span>
                  <span>
                    <span className={styles.loginOptionTitle}>Kid</span>
                    <span className={styles.loginOptionSub}>Family code + PIN</span>
                  </span>
                </button>
              </div>,
              document.body
            )}
          </div>
        </nav>

        <div className={styles.heroContent}>
          {/* Left: pitch */}
          <div className={styles.heroPitch}>
            <div className={styles.eyebrow}>FAMILY REWARDS APP</div>
            <h1 className={styles.headline}>
              Turn Chores into<br />
              <span className={styles.gold}>Epic Adventures</span>
            </h1>
            <p className={styles.subline}>
              Kids earn real rewards for completing tasks. Parents stay in control — approve, set limits, and cash out.
            </p>
            <div className={styles.heroCtas}>
              <button className={styles.primaryBtn} onClick={() => navigate("/setup")}>
                Get Started Free →
              </button>
              <button className={styles.secondaryBtn} onClick={() => scrollToSection("how-it-works")}>
                See how it works
              </button>
            </div>
          </div>

          {/* Right: animated app preview */}
          <div className={styles.previewCard} style={{ borderColor: `${active.visual.accent}50` }}>
            <div className={styles.previewHead}>
              <span style={{ fontSize: 30 }}>{active.emoji}</span>
              <span className={styles.previewLabel} style={{ color: active.visual.accent }}>
                {active.name} Theme
              </span>
            </div>
            <div className={styles.previewTasks}>
              {(["🧹 Clean Kitchen", "📚 Reading Time"] as const).map((name, idx) => (
                <div
                  key={name}
                  className={styles.previewTask}
                  style={{
                    borderColor: `${active.visual.accent}30`,
                    background:  `${active.visual.accent}08`,
                  }}
                >
                  <span className={styles.previewTaskName}>
                    {name} {active.challengeName}
                  </span>
                  <span style={{ color: active.visual.accent, fontSize: 11, fontWeight: 700 }}>
                    +{idx === 0 ? 50 : 30} {active.pointEmoji}
                  </span>
                </div>
              ))}
            </div>
            <div
              className={styles.previewBalance}
              style={{ background: `${active.visual.accent}12` }}
            >
              <span style={{ color: active.visual.accent }}>
                {active.pointEmoji} 320 {active.pointNamePlural} · $3.20 value
              </span>
            </div>
            <div className={styles.previewDots}>
              {CYCLE_IDS.map((id, i) => (
                <div
                  key={id}
                  className={styles.dot}
                  style={{ background: i === themeIdx ? THEMES[id].visual.accent : "rgba(255,255,255,0.15)" }}
                />
              ))}
            </div>
            <div className={styles.previewCaption}>Explorer · Wizard · Sports · more coming</div>
          </div>
        </div>
      </section>

      {/* ② HOW IT WORKS */}
      <section id="how-it-works" className={styles.howSection}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>How it works</h2>
          <p className={styles.sectionSub}>Three steps. No subscriptions.</p>
        </div>
        <div className={styles.steps}>
          {[
            { icon: "📋", n: 1, title: "Parent sets challenges", desc: "Create chores, assign them to kids, set point rewards and due dates" },
            { icon: "🏆", n: 2, title: "Kid completes & earns",  desc: "Kids submit photo proof. Parents approve. Points add up automatically" },
            { icon: "💸", n: 3, title: "Cash out or shop",       desc: "Convert points to real dollars or let kids redeem from the family store" },
          ].map((s) => (
            <div key={s.n} className={styles.step}>
              <div className={styles.stepBadge}>{s.n}</div>
              <div className={styles.stepIcon}>{s.icon}</div>
              <h3 className={styles.stepTitle}>{s.title}</h3>
              <p className={styles.stepDesc}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ③ FEATURES */}
      <section className={styles.featuresSection}>
        <h2 className={styles.sectionTitle}>Everything your family needs</h2>
        <div className={styles.featureGrid}>
          {[
            { icon: "🎮", title: "Choose your theme",     desc: "Explorer, Wizard, or Sports — each kid picks their own world and visual experience" },
            { icon: "📸", title: "Photo verification",    desc: 'Kids snap a photo as proof of completion. No more "I did it, I promise"' },
            { icon: "👨‍👩‍👧", title: "Multi-parent support", desc: "Both parents review and approve. Invite co-parents with a shareable link" },
            { icon: "💰", title: "Real cash rewards",     desc: "Set your own point-to-dollar rate. Weekly caps. You control how much they earn" },
          ].map((f) => (
            <div key={f.title} className={styles.featureCard}>
              <div className={styles.featureIcon}>{f.icon}</div>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ④ THEME SHOWCASE */}
      <section id="themes" className={styles.themesSection}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Every kid gets their own world</h2>
          <p className={styles.sectionSub}>
            Kids request a theme — parents approve, or it switches automatically after 7 days.
          </p>
        </div>
        <div className={styles.themeCards}>
          {(["explorer", "wizard", "sports"] as const).map((id) => {
            const t = THEMES[id];
            return (
              <div
                key={id}
                className={styles.themeCard}
                style={{
                  background:  `linear-gradient(135deg, ${t.visual.bgFrom}, ${t.visual.bgTo})`,
                  borderColor: `${t.visual.accent}35`,
                }}
              >
                <div className={styles.themeCardEmoji}>{t.emoji}</div>
                <div className={styles.themeCardName} style={{ color: t.visual.accent }}>{t.name}</div>
                <div className={styles.themeCardLabels}>
                  {t.challengeNamePlural} · {t.pointNamePlural}<br />{THEME_TAGLINES[id]}
                </div>
              </div>
            );
          })}
          <div className={`${styles.themeCard} ${styles.themeCardLocked}`}>
            <div className={styles.themeCardEmoji}>✨</div>
            <div className={styles.themeCardName}>More worlds coming</div>
            <div className={styles.themeCardLabels}>New themes added regularly</div>
          </div>
        </div>
      </section>

      {/* ⑤ FOOTER CTA */}
      <section className={styles.footerCta}>
        <h2 className={styles.footerTitle}>Ready to start your family's adventure?</h2>
        <p className={styles.footerSub}>Free to set up. No subscription. Cancel anytime.</p>
        <button className={styles.primaryBtn} onClick={() => navigate("/setup")}>
          Get Started Free →
        </button>
        <p className={styles.footerNote}>
          Already have an account?{" "}
          <button className={styles.footerLoginLink} onClick={() => navigate("/login")}>Parent Login</button>
          {" · "}
          <button className={styles.footerLoginLink} onClick={() => navigate("/kid/login")}>Kid Login</button>
        </p>
      </section>

    </div>
  );
}
